import { TranslationKeys } from "../../contexts/TranslationKeys";
import { Translator } from "../../contexts/translator";
import { Days } from "../../models/Day";

export const getDays = (monday: number, tuesday: number, wednesday: number, thursday: number, friday: number,  saturday: number, sunday: number, holiday: number, translator: Translator): string => {
    let days = monday !== 0 ? `${translator.byKey(TranslationKeys.mon)}\t` : '';
    days += tuesday !== 0 ? `${translator.byKey(TranslationKeys.tue)}\t` : '';
    days += wednesday !== 0 ? `${translator.byKey(TranslationKeys.wed)}\t` : '';
    days += thursday !== 0 ? `${translator.byKey(TranslationKeys.thu)}\t` : '';
    days += friday !== 0 ? `${translator.byKey(TranslationKeys.fri)}\t` : '';
    days += saturday !== 0 ? `${translator.byKey(TranslationKeys.sat)}\t` : '';
    days += sunday !== 0 ? `${translator.byKey(TranslationKeys.sun)}\t` : '';
    days += holiday !== 0 ? `${translator.byKey(TranslationKeys.hol)}\t` : '';
    return days;
}

export const setTime = (timeString: string): Date => {
    const hoursAndMinutes = timeString.split(':');
    const time = new Date();
    if(hoursAndMinutes.length > 0) {
        time.setHours(parseInt(hoursAndMinutes[0]));
        time.setMinutes(parseInt(hoursAndMinutes[1]));
    }

    return time;
}

export const convertToTime = (time: string): string => {
    const newTime = time.toString();
    return newTime.length === 3 ? `0${newTime[0]}:${newTime[1]}${newTime[2]}\t` : newTime.length === 4 ? `${newTime[0]}${newTime[1]}:${newTime[2]}${newTime[3]}` : '--:--';
}

export const convertToEndTime = (startTime: string, duration: string): string => {
    const convertedEndTime = parseInt(startTime) + parseInt(duration);
    return convertToTime(convertedEndTime.toString());
}

export const getFormattedDate = (date: string): string => {
    const newDate = date.toString();
    switch (newDate.length) {
        case 6:
            return `${newDate[0]}${newDate[1]}/${newDate[2]}${newDate[3]}/${newDate[4]}${newDate[5]}`;
        case 8:
            return `${newDate[0]}${newDate[1]}${newDate[2]}${newDate[3]}/${newDate[4]}${newDate[5]}/${newDate[6]}${newDate[7]}`;
        case 10:
            return `${newDate[0]}${newDate[1]}${newDate[2]}${newDate[3]}/${newDate[5]}${newDate[6]}/${newDate[8]}${newDate[9]}`;
        default:
            return '';
    }
}

/**
 * A simple helper function that converts a string like "20250919T103259" or "2025-10-03T13:05.12.123"
 * into a formatted date-time string like "2025/09/19 10:32:59":
 * @param input 
 */
export const getFormattedDateTime = (input: string): string => {
    // Handle two formats:
    // 1. 'YYYYMMDDTHHMMSS' (length 15, no delimiters except T)
    // 2. 'YYYY-MM-DDTHH:MM.SS.SSS' (ISO-like, with delimiters)
    if (!input) return '';

    // Format 1: 20250919T103259
    if (input.length === 15 && input[8] === 'T') {
        const year = input.substring(0, 4);
        const month = input.substring(4, 6);
        const day = input.substring(6, 8);
        const hour = input.substring(9, 11);
        const minute = input.substring(11, 13);
        const second = input.substring(13, 15);
        return `${year}/${month}/${day} ${hour}:${minute}:${second}`;
    }

    // Format 2: 2025-10-03T13:05.12.123
    // Regex: YYYY-MM-DDTHH:MM.SS(.SSS)?
    const isoLikeRegex = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})\.(\d{2})(?:\.(\d{1,3}))?$/;
    const match = input.match(isoLikeRegex);
    if (match) {
        const [, year, month, day, hour, minute, second] = match;
        return `${year}/${month}/${day} ${hour}:${minute}:${second}`;
    }

    return 'Invalid format';
};

export const getUnFormattedDate = (date: string): string => {
    let newDate = date.toString();
    newDate = newDate.replaceAll("-", "");
    return newDate;
}

export const convertToDuration = (duration: string): string => {
    const newDuration = duration.toString();
    return newDuration.length === 1 && newDuration !== '0' ? `00h:0${newDuration}m`
        : newDuration.length === 2 ? `00h:${newDuration}m`
            : newDuration.length === 3 ? `0${newDuration[0]}h:${newDuration[1]}${newDuration[2]}m`
                : newDuration.length === 4 ? `${newDuration[0]}${newDuration[1]}h:${newDuration[2]}${newDuration[3]}m`
                    : '24h:00m';
}

export const getTime = (date: Date): string => {
    try {
        const hours = date.getHours();
        const minutes = date.getMinutes();
        return `${hours > 9 ? hours : `0${hours}`}${minutes > 9 ? minutes : `0${minutes}`}`;
    }
    catch (error) {
        console.error("An error occurred while attempting to get the time from a date. Error: ", error);
        return "0";
    }    
}

export const getFormattedTime = (time: string): string => {
    if (time.length === 4) {
        return `${time[0]}${time[1]}:${time[2]}${time[3]}`
    }

    return "00:00"
}

export const getFormattedTimeFromNum = (time: number): string => {
    const timeString = time.toString();

    // Check if time matches hh:mm:ss format
    const regex = /^\d{2}:\d{2}:\d{2}$/;
    if (regex.test(timeString)) {
        return timeString;
    }
    // HHMMSS (e.g., 83401 = 08:34:01; 102309 = 10:23:09)
    if (timeString.length === 5 || timeString.length === 6) {
        const prefix = timeString.length === 5 ? "0" : timeString[0];
        const timeIndex = timeString.length === 5 ? 0 : 1;
        const hours = `${prefix}${timeString[timeIndex]}`;
        const mins = `${timeString[timeIndex+1]}${timeString[timeIndex+2]}`;
        const secs = `${timeString[timeIndex+2]}${timeString[timeIndex+3]}`;
        return `${hours}:${mins}:${secs}`
    }
    // HHMM (e.g., 830 = 08:30; 1112 = 11:12)
    if (timeString.length === 3 || timeString.length === 4) {
        const prefix = timeString.length === 3 ? "0" : timeString[0];
        const timeIndex = timeString.length === 3 ? 0 : 1;
        const hours = `${prefix}${timeString[timeIndex]}`;
        const mins = `${timeString[timeIndex+1]}${timeString[timeIndex+2]}`;
        return `${hours}:${mins}`
    }

    return "00:00";
}

export const calculateDuration = (startDate: Date, endDate: Date): string => {
    if (!startDate || !endDate || Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
        return "2400";
    }

    const minutesInDay = 24 * 60;
    const startMinutes = (startDate.getHours() * 60) + startDate.getMinutes();
    const endMinutes = (endDate.getHours() * 60) + endDate.getMinutes();

    let durationMinutes = endMinutes - startMinutes;
    if (durationMinutes < 0) {
        durationMinutes += minutesInDay;
    }

    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;

    return `${hours > 9 ? hours : `0${hours}`}${minutes > 9 ? minutes : `0${minutes}`}`;
}

export const buildDayList = (monday: boolean, tuesday: boolean, wednesday: boolean, thursday: boolean, friday: boolean, saturday: boolean, sunday: boolean, holidays: boolean): string[] => {
    return [
        monday ? Days.monday : '',
        tuesday ? Days.tuesday : '',
        wednesday ? Days.wednesday : '',
        thursday ? Days.thursday : '',
        friday ? Days.friday : '',
        saturday ? Days.saturday : '',
        sunday ? Days.sunday : '',
        holidays ? Days.holidays : '',
    ].filter(day => !!day);
}

export const getDayOfWeekByDay = (dayOfWeek) => {
    const days = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
    const currentDate = new Date();
    const date = `${currentDate.getMonth() + 1}` + "/" + `${dayOfWeek}` + "/" + `${currentDate.getFullYear()}`;
    const formatedCurrentDate = new Date(date);
    return days[formatedCurrentDate.getDay()];
}

// hh:mm
export function formatTime(inputDate: string): string {
    const date = new Date(inputDate);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${hours}:${minutes}`;
}

export const convertDateToString = (date: Date): string => {
    if (date) {
        const day: string = date.getDate().toString().padStart(2, '0');
        const month: string = (date.getMonth() + 1).toString().padStart(2, '0');
        const year: string = date.getFullYear().toString();

        return `${year}/${month}/${day}`;
    }
    return "";
}